import React from 'react';
import { Container } from '../../../components/Container/Container';
import { CampaignDetails } from './details';
import { Activities } from '../../../components/Activities/Activities';
import { useParams } from 'react-router-dom';
import { Stack } from '../../../components/Stack/Stack';
import { graphql } from '../../../generated';
import { useQueryHandler } from '../../../model/graphql';
import { t } from '../../../locales/translate';
import { voucherCampaignsPath } from '../../../routes';
import { Breadcrumbs } from '../../../components/breadcrumbs/breadcrumbs';
import { Text } from '../../../components/text/text';
import { Automations } from '../../../components/automations/automations';
import { useConfirmation } from '../../../components/ConfirmationPrompt/ConfirmationPrompt';
import { useMutation } from '@apollo/client';
import { history } from '../../../model/history';
import { Button } from '../../../components/Button/Button';
import { StatusPill } from '../../../components/StatusPill/StatusPill';
import { VoucherCampaign as VoucherCampaignType } from '../../../generated/graphql';
import { getVoucherCampaignStatus } from '../util';
import { VoucherCodesList } from './voucher_codes/voucher_codes_list';
import { getPageTitle, useMeta } from '../../../model/meta';
import { DiscountedProductsAndEvents } from './discounted_products_and_events';

const VOUCHER_CAMPAIGN = graphql(`
  query VoucherCampaign($id: ID!) {
    voucherCampaign(id: $id) {
      id
      title
      internalTitle
      voucherCodesCount
      status
      automations {
        nodes {
          id
          operation
          executeAt
          permissions {
            id
            destroy
            update
          }
        }
        permissions {
          id
          create
        }
      }
      permissions {
        id
        destroy
        start
        stop
      }
    }
  }
`);

const DESTROY_VOUCHER_CAMPAIGN = graphql(`
  mutation DestroyVoucherCampaign($id: ID!) {
    destroyVoucherCampaign(input: { id: $id }) {
      clientMutationId
    }
  }
`);

const START_CAMPAIGN = graphql(`
  mutation StartCampaign($id: ID!) {
    startVoucherCampaign(input: { id: $id }) {
      voucherCampaign {
        id
        status
      }
    }
  }
`);

const STOP_CAMPAIGN = graphql(`
  mutation StopCampaign($id: ID!) {
    stopVoucherCampaign(input: { id: $id }) {
      voucherCampaign {
        id
        status
      }
    }
  }
`);

export function VoucherCampaign(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { data, loading } = useQueryHandler(VOUCHER_CAMPAIGN, { variables: { id } });

  const { title, internalTitle, automations, permissions, status } = data?.voucherCampaign || {};

  useMeta({
    title: getPageTitle(loading, title, data?.voucherCampaign),
    breadcrumbs: [
      { title: t.page.voucherCampaign.voucherCampaigns, url: voucherCampaignsPath() },
      {
        title: getPageTitle(loading, title, data?.voucherCampaign),
      },
    ],
  });

  const [destroy] = useMutation(DESTROY_VOUCHER_CAMPAIGN, { variables: { id } });

  const [start] = useMutation(START_CAMPAIGN, { variables: { id }, refetchQueries: [VOUCHER_CAMPAIGN] });
  const [stop] = useMutation(STOP_CAMPAIGN, { variables: { id }, refetchQueries: [VOUCHER_CAMPAIGN] });

  const { confirm } = useConfirmation({
    title: t.page.voucherCampaign.delete.title,
    message: t.page.voucherCampaign.delete.description,
    actionName: t.page.voucherCampaign.delete.confirm,
    action: async () => {
      if (!data?.voucherCampaign) return;
      await destroy();
      history.push(voucherCampaignsPath());
    },
  });

  return (
    <Container>
      <Stack size="xl">
        <Stack>
          <Breadcrumbs />
          <header>
            <Stack line justify="space-between">
              <Stack line size="xxs">
                <Text type="page-title" bold uppercase inline>
                  {title}
                </Text>
                {internalTitle && (
                  <Text type="page-title" inline>
                    ({internalTitle})
                  </Text>
                )}
                {<StatusPill status={getVoucherCampaignStatus(data?.voucherCampaign as VoucherCampaignType)} />}
              </Stack>
              <Stack line size="xxs">
                <Button type="icon" icon="trash" disabled={!permissions?.destroy} onClick={confirm} />
                <Automations
                  automatableId={id}
                  automatableType="VOUCHER_CAMPAIGN"
                  automations={automations?.nodes}
                  permissions={{
                    start: permissions?.start,
                    stop: permissions?.stop,
                  }}
                  onStart={start}
                  onStop={stop}
                  running={status === 'ACTIVE'}
                />
              </Stack>
            </Stack>
          </header>
        </Stack>
        <CampaignDetails />
        <VoucherCodesList />
        <DiscountedProductsAndEvents />
        <Activities subjectType="voucher_campaign" subjectId={id} />
      </Stack>
    </Container>
  );
}
